import { Fragment, useState } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

// Mirrors AnnotationsSummaryTab's shape so the admin tab pair feels consistent:
// per-study accordion, top stats row, study filter, CSV export. Runs are
// grouped by study and each row exposes the full prompt/answer behind a
// "View" expander — listing all the QA text inline would push everything else
// off the page on bigger projects.
const ModelRunsSummaryTab = ({
    runs,
    summary,
    studies,
    loading,
    filters,
    onFilterChange,
    projectId,
    onRefresh,
}) => {
    const [expandedStudies, setExpandedStudies] = useState({});
    const [expandedRuns, setExpandedRuns] = useState({});
    const [exporting, setExporting] = useState(false);
    const [deletingRun, setDeletingRun] = useState(null);

    const handleDeleteRun = async (run) => {
        const label = `${run.patient_id || "(no patient)"} — ${run.model_name || "model"}`;
        if (!window.confirm(`Delete model run for ${label}? This cannot be undone.`)) return;
        try {
            setDeletingRun(run.id);
            await API.delete(`/projects/${projectId}/model-runs/${run.id}/`);
            toast.success("Model run deleted.");
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error("Error deleting model run:", err);
            toast.error(err.response?.data?.error || "Failed to delete model run.");
        } finally {
            setDeletingRun(null);
        }
    };

    const groupedByStudy = runs.reduce((acc, r) => {
        const sid = r.study_id ?? "unassigned";
        if (!acc[sid]) {
            acc[sid] = { studyName: r.study_name || "(no study)", runs: [] };
        }
        acc[sid].runs.push(r);
        return acc;
    }, {});

    const toggleStudy = (sid) => setExpandedStudies((p) => ({ ...p, [sid]: !p[sid] }));
    const toggleRun = (id) => setExpandedRuns((p) => ({ ...p, [id]: !p[id] }));

    const handleExport = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams();
            if (filters.studyId) params.append("study_id", filters.studyId);
            const response = await API.get(
                `/projects/${projectId}/model-runs/export/?${params.toString()}`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `model_runs_${new Date().toISOString().split("T")[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Model runs exported successfully!");
        } catch (err) {
            console.error("Error exporting model runs:", err);
            toast.error("Failed to export model runs.");
        } finally {
            setExporting(false);
        }
    };

    const statusBadge = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "completed") return <span className="badge bg-success">Completed</span>;
        if (s === "failed" || s === "error") return <span className="badge bg-danger">Failed</span>;
        if (s === "cancelled") return <span className="badge bg-secondary">Cancelled</span>;
        if (s === "running" || s === "preparing" || s === "pending") return <span className="badge bg-info text-dark">{status}</span>;
        return <span className="badge bg-light text-dark">{status || "-"}</span>;
    };

    const formatDuration = (s) => {
        if (s === null || s === undefined) return "-";
        if (s < 60) return `${Math.round(s)}s`;
        const m = Math.floor(s / 60);
        const r = Math.round(s % 60);
        return `${m}m ${r}s`;
    };

    const contextBadges = (run) => {
        const pc = run.patient_context_summary;
        if (!pc) return <span className="text-muted">-</span>;
        const items = [];
        if (pc.images_count) items.push(<span key="i" className="badge bg-info text-dark me-1" title="Imaging screenshots">📷 {pc.images_count}</span>);
        if (pc.documents_count) items.push(<span key="d" className="badge bg-warning text-dark me-1" title="Document text">📄 {pc.documents_count}</span>);
        if (pc.imaging_metadata_count) items.push(<span key="m" className="badge bg-light text-dark me-1" title="Imaging metadata">🗂 {pc.imaging_metadata_count}</span>);
        return items.length ? <>{items}</> : <span className="text-muted">-</span>;
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading model runs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Model Run History</h5>
                <button
                    className="btn btn-success btn-sm"
                    onClick={handleExport}
                    disabled={runs.length === 0 || exporting}
                >
                    {exporting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Exporting...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-download me-1"></i>
                            Export CSV
                        </>
                    )}
                </button>
            </div>

            <div className="card-body">
                {/* Summary stats */}
                {summary && (
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.total_runs}</h4>
                                <small className="text-muted">Total Runs</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.unique_patients}</h4>
                                <small className="text-muted">Unique Patients</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.unique_users}</h4>
                                <small className="text-muted">Reviewers</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.studies_with_runs}</h4>
                                <small className="text-muted">Studies with Runs</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filter */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <label className="form-label small text-muted">Filter by Study</label>
                        <select
                            className="form-select"
                            value={filters.studyId}
                            onChange={(e) => onFilterChange({ ...filters, studyId: e.target.value })}
                        >
                            <option value="">All Studies</option>
                            {studies.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Table */}
                {runs.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fa fa-robot mb-3" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
                        <p>No model runs yet.</p>
                        <small>Runs will appear here once reviewers use AI panels in study viewers.</small>
                    </div>
                ) : (
                    <div className="accordion" id="modelRunsAccordion">
                        {Object.entries(groupedByStudy).map(([sid, data]) => (
                            <div className="accordion-item" key={sid}>
                                <h2 className="accordion-header">
                                    <button
                                        className={`accordion-button ${expandedStudies[sid] ? "" : "collapsed"}`}
                                        type="button"
                                        onClick={() => toggleStudy(sid)}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <i className={`fa fa-chevron-${expandedStudies[sid] ? "down" : "right"}`}></i>
                                            <span className="fw-bold">{data.studyName}</span>
                                            <span className="badge bg-secondary">
                                                {data.runs.length} run(s)
                                            </span>
                                        </div>
                                    </button>
                                </h2>
                                <div className={`accordion-collapse collapse ${expandedStudies[sid] ? "show" : ""}`}>
                                    <div className="accordion-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Patient ID</th>
                                                        <th>Model</th>
                                                        <th>Reviewer</th>
                                                        <th>Status</th>
                                                        <th>Context</th>
                                                        <th>Citations</th>
                                                        <th>Duration</th>
                                                        <th>Run At</th>
                                                        <th>Prompt / Answer</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.runs.map((r) => {
                                                        const open = !!expandedRuns[r.id];
                                                        return (
                                                            <Fragment key={r.id}>
                                                                <tr>
                                                                    <td><code>{r.patient_id}</code></td>
                                                                    <td>{r.model_name || <span className="text-muted">-</span>}</td>
                                                                    <td>{r.initiated_by_username || <span className="text-muted">-</span>}</td>
                                                                    <td>{statusBadge(r.status)}</td>
                                                                    <td>{contextBadges(r)}</td>
                                                                    <td>
                                                                        {r.citations_count > 0 ? (
                                                                            <span className="badge bg-primary">{r.citations_count}</span>
                                                                        ) : (
                                                                            <span className="text-muted">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{formatDuration(r.duration_seconds)}</td>
                                                                    <td>
                                                                        <small className="text-muted">
                                                                            {r.created_at ? new Date(r.created_at).toLocaleString() : "-"}
                                                                        </small>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-outline-secondary btn-sm"
                                                                                onClick={() => toggleRun(r.id)}
                                                                                disabled={!r.query && !r.answer && !r.error_message}
                                                                            >
                                                                                <i className={`fa fa-chevron-${open ? "up" : "down"} me-1`}></i>
                                                                                {open ? "Hide" : "View"}
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-outline-danger btn-sm"
                                                                                onClick={() => handleDeleteRun(r)}
                                                                                disabled={deletingRun === r.id}
                                                                                title="Delete model run"
                                                                            >
                                                                                {deletingRun === r.id ? (
                                                                                    <span className="spinner-border spinner-border-sm"></span>
                                                                                ) : (
                                                                                    <i className="fa fa-trash"></i>
                                                                                )}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                {open && (
                                                                    <tr>
                                                                        <td colSpan={9} style={{ background: "#f7f9fc" }}>
                                                                            {r.query && (
                                                                                <div className="mb-2">
                                                                                    <div className="fw-bold text-muted small">Prompt</div>
                                                                                    <div style={{ whiteSpace: "pre-wrap" }}>{r.query}</div>
                                                                                </div>
                                                                            )}
                                                                            {r.answer && (
                                                                                <div className="mb-2">
                                                                                    <div className="fw-bold text-muted small">Answer</div>
                                                                                    <div style={{ whiteSpace: "pre-wrap" }}>{r.answer}</div>
                                                                                </div>
                                                                            )}
                                                                            {r.error_message && (
                                                                                <div className="mb-2 text-danger">
                                                                                    <div className="fw-bold small">Error</div>
                                                                                    <div style={{ whiteSpace: "pre-wrap" }}>{r.error_message}</div>
                                                                                </div>
                                                                            )}
                                                                            {Array.isArray(r.citations) && r.citations.length > 0 && (
                                                                                <div className="mb-2">
                                                                                    <div className="fw-bold text-muted small">Citations</div>
                                                                                    <ol className="ps-3 mb-0">
                                                                                        {r.citations.map((c, ci) => (
                                                                                            <li key={ci} value={c?.index ?? ci}>
                                                                                                {c?.url ? (
                                                                                                    <a href={c.url} target="_blank" rel="noopener noreferrer">
                                                                                                        {c.title || c.url}
                                                                                                    </a>
                                                                                                ) : (
                                                                                                    c?.title || "Untitled"
                                                                                                )}
                                                                                                {c?.authors && <span className="text-muted"> — {c.authors}</span>}
                                                                                                {c?.year && <span className="text-muted"> · {c.year}</span>}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModelRunsSummaryTab;
